import React, { useEffect, useState } from 'react';
import { DraggableData, DraggableEvent } from 'react-draggable';
import { ScheduledMessage, ScheduledMessageForm } from '@asset/type/chat';
import { useChatStore } from '@store/chat';
import ChatPopupWrapper from '../ChatPopupWrapper';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { ScheduledMessageFormKey } from '@asset/enum/chatEnum';
import { useMutation, useQuery } from 'react-query';
import { uploadChatFiles } from '@asset/function/chat';
import { toast } from 'react-toastify';
import { patchScheduledMessage, postScheduledMessage } from '@apis/api/chat';
import { DateTime } from 'luxon';
import { urlToFileObject } from '@asset/function/commonFunctions';
import _ from 'lodash';
import IconContainer from '@components/common/IconContainer';
import CloseIcon from '@asset/svg/CloseIcon';
import { DateTimePicker } from '@components/common/DateTimePicker';
import BasicButton from '@components/common/button/BasicButton';
import { getStudentList } from '@apis/api/dashboard';
import * as S from '@styles/chat/scheduleMessage.style';
import StudentSelector from './StudentSelector';
import { useUserInfoQuery } from '@apis/query/user';
import AttachImageFormV2 from '@components/common/form/AttageImageFormV2';
import { FileInfo } from '@asset/type/file';

const ScheduledMessageFormPopup = ({
  popupRef,
  selectedMessage,
  defaultPosition,
  scheduledMessageForm,
  isTablet,
  files,
  onDrag,
  togglePopupVisible,
  openScheduledMessageListPopup,
  onSuccessRegisterScheduledMessage,
  onDeleteScheduledMessage,
  setFiles,
}: {
  popupRef: React.RefObject<HTMLDivElement>;
  selectedMessage: ScheduledMessage;
  scheduledMessageForm: UseFormReturn<ScheduledMessageForm>;
  defaultPosition: { x: string; y: string };
  isTablet: boolean;
  files: FileInfo[];
  onDrag: (e: DraggableEvent, data: DraggableData) => void;
  togglePopupVisible: (visible: boolean) => void;
  openScheduledMessageListPopup: () => void;
  onSuccessRegisterScheduledMessage: () => void;
  onDeleteScheduledMessage: (message: ScheduledMessage) => void;
  setFiles: React.Dispatch<React.SetStateAction<FileInfo[]>>;
}) => {
  const { data: user } = useUserInfoQuery();
  const { selectedChannel } = useChatStore();
  const selectedChannelInfo = selectedMessage
    ? {
        url: selectedMessage.channelUrl,
        studentName: selectedMessage.studentName,
      }
    : selectedChannel;

  const [hasChange, setHasChange] = useState(false);

  const resetForm = () => {
    scheduledMessageForm.setValue(ScheduledMessageFormKey.RECIPIENT, {
      name: selectedChannelInfo.studentName,
      channelUrl: selectedChannelInfo.url,
    });
    scheduledMessageForm.setValue(ScheduledMessageFormKey.MESSAGE, '');
    setFiles([]);
    scheduledMessageForm.setValue(ScheduledMessageFormKey.SCHEDULED_AT, null);
  };

  const { data: students } = useQuery(
    ['GET_STUDENT_LIST'],
    async () => {
      const response = await getStudentList({
        pageSize: 10000,
        status: '재원',
        teacherId: user?.id,
      });
      if (response.success) {
        return _.filter(
          response.students,
          (student) => !_.isNil(student.sendbirdChannelUrl),
        );
      }
      return [];
    },
    { initialData: [], enabled: !!user?.id },
  );

  const syncFilesToRemoteStorage = async (): Promise<FileInfo[]> => {
    const remoteFiles = files.filter((file) => file.isRemote);
    const localFiles = await Promise.all(
      files
        .filter((file) => !file.isRemote)
        .map((file) => urlToFileObject(file.url)),
    );
    const newRemoteFiles = (
      await uploadChatFiles(localFiles, () =>
        toast.error('일부 사진 업로드에 실패했습니다.'),
      )
    ).map((file) => ({
      url: file.url,
      isRemote: true,
    }));
    return [...remoteFiles, ...newRemoteFiles];
  };

  const {
    isLoading: isLoadingRegisterScheduledMessage,
    mutateAsync: registerScheduledMessage,
  } = useMutation(
    ['REGISTER_SCHEDULED_MESSAGE', selectedChannelInfo?.url],
    async (formResult: ScheduledMessageForm) => {
      if (!_.isNil(selectedChannelInfo?.url)) {
        const fileUrls = (await syncFilesToRemoteStorage()).map((file) => ({
          url: file.url,
        }));

        const postScheduledMessageResponse = await postScheduledMessage(
          formResult[ScheduledMessageFormKey.RECIPIENT].channelUrl,
          {
            ...formResult,
            files: fileUrls,
          },
        );
        if (postScheduledMessageResponse.success) {
          resetForm();
          onSuccessRegisterScheduledMessage();
        }
      }
    },
  );

  const {
    isLoading: isLoadingUpdateScheduledMessage,
    mutateAsync: updateScheduledMessage,
  } = useMutation(
    ['UPDATE_SCHEDULED_MESSAGE'],
    async (formResult: ScheduledMessageForm) => {
      const _requestBody = {};

      const {
        isRecipientDifferent,
        isMessageDifferent,
        areFilesDifferent,
        isScheduledAtDifferent,
      } = await checkDifferenceWithMessage(selectedMessage);
      if (isRecipientDifferent) {
        _requestBody['channelUrl'] =
          formResult[ScheduledMessageFormKey.RECIPIENT].channelUrl;
      }
      if (isMessageDifferent) {
        _requestBody[ScheduledMessageFormKey.MESSAGE] =
          formResult[ScheduledMessageFormKey.MESSAGE];
      }

      if (areFilesDifferent) {
        _requestBody['files'] = (await syncFilesToRemoteStorage()).map(
          (file) => ({
            url: file.url,
          }),
        );
      }

      if (isScheduledAtDifferent) {
        _requestBody[ScheduledMessageFormKey.SCHEDULED_AT] =
          formResult[ScheduledMessageFormKey.SCHEDULED_AT];
      }

      const patchScheduledMessageResponse = await patchScheduledMessage(
        selectedMessage.id,
        _requestBody,
      );
      if (patchScheduledMessageResponse.success) {
        resetForm();
        onSuccessRegisterScheduledMessage();
      }
    },
  );

  const isFormValid =
    !isLoadingRegisterScheduledMessage &&
    !isLoadingUpdateScheduledMessage &&
    scheduledMessageForm.formState.isValid &&
    !_.isNil(scheduledMessageForm.watch(ScheduledMessageFormKey.SCHEDULED_AT));

  const setupMessageFormData = async (message: ScheduledMessage) => {
    scheduledMessageForm.setValue(ScheduledMessageFormKey.RECIPIENT, {
      name: message.studentName,
      channelUrl: message.channelUrl,
    });
    scheduledMessageForm.setValue(
      ScheduledMessageFormKey.MESSAGE,
      message.message,
    );
    scheduledMessageForm.setValue(
      ScheduledMessageFormKey.SCHEDULED_AT,
      DateTime.fromISO(message.scheduledAt),
    );
    setFiles(
      message.files.map((file) => ({
        url: file.url,
        isRemote: true,
      })),
    );
  };

  useEffect(() => {
    if (!_.isNil(selectedMessage)) {
      setupMessageFormData(selectedMessage);
    } else {
      resetForm();
    }
  }, [selectedMessage]);

  const checkDifferenceWithMessage = async (
    targetMessage: ScheduledMessage,
  ): Promise<{
    isRecipientDifferent: boolean;
    isMessageDifferent: boolean;
    isScheduledAtDifferent: boolean;
    areFilesDifferent: boolean;
  }> => {
    const formValues = scheduledMessageForm.getValues();
    const isRecipientDifferent =
      targetMessage.channelUrl !==
      formValues[ScheduledMessageFormKey.RECIPIENT]?.channelUrl;

    const isMessageDifferent =
      targetMessage.message !== formValues[ScheduledMessageFormKey.MESSAGE];

    const localFileAdded = files.some((file) => !file.isRemote);
    const remoteFileDeleted =
      targetMessage.files.length !==
      files.filter((file) => file.isRemote).length;
    const areFilesDifferent = localFileAdded || remoteFileDeleted;

    const isScheduledAtDifferent = !DateTime.fromISO(
      targetMessage.scheduledAt,
    ).equals(formValues[ScheduledMessageFormKey.SCHEDULED_AT]);
    return {
      isRecipientDifferent,
      isMessageDifferent,
      areFilesDifferent,
      isScheduledAtDifferent,
    };
  };

  useEffect(() => {
    const checkDifference = async () => {
      if (!_.isNil(selectedMessage)) {
        const {
          isRecipientDifferent,
          isMessageDifferent,
          isScheduledAtDifferent,
          areFilesDifferent,
        } = await checkDifferenceWithMessage(selectedMessage);
        setHasChange(
          isRecipientDifferent ||
            isMessageDifferent ||
            isScheduledAtDifferent ||
            areFilesDifferent,
        );
      }
    };
    checkDifference();
  }, [scheduledMessageForm.watch()]);

  return (
    <ChatPopupWrapper
      draggableCoreProps={{ onDrag }}
      popupRef={popupRef}
      defaultPosition={defaultPosition}
      rndProps={{
        enableResizing: { bottom: !isTablet },
      }}
    >
      <section className="flex flex-col h-full">
        <S.ScheduledMessagePopupHeader className="drag-handle">
          <div className="middle title">메시지 예약</div>
          <IconContainer
            icon={<CloseIcon width="20" height="20" color="var(--gray-10)" />}
            size={'M'}
            onClick={() => togglePopupVisible(false)}
          />
        </S.ScheduledMessagePopupHeader>

        <S.ScheduledMessageForm
          onSubmit={scheduledMessageForm.handleSubmit(async (data) => {
            if (_.isNil(selectedMessage)) {
              await registerScheduledMessage(data);
            } else {
              await updateScheduledMessage(data);
            }
          })}
        >
          <FormProvider {...scheduledMessageForm}>
            <div className="wrapper">
              <StudentSelector
                scheduledMessageForm={scheduledMessageForm}
                students={students}
              />

              <S.MessageTextArea>
                <textarea
                  {...scheduledMessageForm.register(
                    ScheduledMessageFormKey.MESSAGE,
                    { required: true },
                  )}
                  placeholder="예약 메시지를 입력하세요."
                ></textarea>
              </S.MessageTextArea>

              <S.AdvancedForm>
                <div className="attach-image">
                  <AttachImageFormV2
                    isEditMode
                    files={files}
                    onAddFiles={(addedFiles) => {
                      setFiles((prev) => [...prev, ...addedFiles]);
                    }}
                    onDeleteFiles={(deletedFiles) => {
                      setFiles((prev) =>
                        prev.filter((file) => !deletedFiles.includes(file)),
                      );
                    }}
                  ></AttachImageFormV2>
                </div>
                <div className="time">
                  <div className="title">예약 시간</div>
                  <DateTimePicker
                    disabled={{ before: DateTime.now() }}
                    defaultDateTime={
                      selectedMessage
                        ? DateTime.fromISO(
                            selectedMessage[
                              ScheduledMessageFormKey.SCHEDULED_AT
                            ],
                          )
                        : null
                    }
                    onTimeChange={(date) => {
                      scheduledMessageForm.setValue(
                        ScheduledMessageFormKey.SCHEDULED_AT,
                        date,
                        { shouldDirty: true },
                      );
                    }}
                  />
                </div>
              </S.AdvancedForm>
            </div>
            <S.ScheduledMessagePopupFooter>
              <div>
                <BasicButton
                  color={'gray'}
                  assetType={'Secondary'}
                  size={'Xs'}
                  content={'예약 목록'}
                  onClickEvent={() => openScheduledMessageListPopup()}
                />
              </div>
              <div>
                {selectedMessage ? (
                  <>
                    <BasicButton
                      color={'red'}
                      assetType={'Secondary'}
                      size={'Xs'}
                      content={'삭제'}
                      onClickEvent={() =>
                        onDeleteScheduledMessage(selectedMessage)
                      }
                    />
                    <BasicButton
                      color={'blue'}
                      assetType={'Primary'}
                      size={'Xs'}
                      content={'저장'}
                      isActive={isFormValid && hasChange}
                      buttonType="submit"
                    />
                  </>
                ) : (
                  <BasicButton
                    color={'blue'}
                    assetType={'Primary'}
                    size={'Xs'}
                    content={'예약'}
                    isActive={isFormValid}
                    buttonType="submit"
                  />
                )}
              </div>
            </S.ScheduledMessagePopupFooter>
          </FormProvider>
        </S.ScheduledMessageForm>
      </section>
    </ChatPopupWrapper>
  );
};

export default ScheduledMessageFormPopup;
